import type { ComponentType } from 'react';

import type { RegisteredView } from '@/features/registry';
import type { DocumentSnapshot, FeatureApi } from '@/features/types';
import type { ShellView, ViewTab } from '@/ui/shell/types';

import type { Translate } from './commandContext';

/**
 * `ViewDescriptor` → what the shell accepts — ADR-0020, docs/ARCHITECTURE.md §8,
 * `src/ui/shell/types.ts`.
 *
 * The shell may not import `@/features/**` (gate 2) and a feature may not resolve an i18n key
 * or run `select` for a document it was not given. This file is the hinge between the two, and
 * it is the ONE place in the repository that re-pairs a view's `select` output with the panel
 * that consumes it.
 *
 * ═══ THE ONE ASSERTION, AND WHY IT HOLDS ═══
 *
 * `registry.ts` stores descriptors as `RegisteredView`, whose `select` returns `unknown` and
 * whose module default is `unknown` — the erasure that lets descriptors with different view
 * types share one array without `any` (see the note there). That is what makes the pair safe
 * to STORE and impossible to RENDER, so somewhere the two halves have to be reunited, and that
 * is an assertion no matter how it is spelled.
 *
 * It is sound, and the argument is short enough to check. At run time the object handed to the
 * panel is `{ view: registered.select(doc), api }` — literally the result of THIS descriptor's
 * own `select`, handed to THIS descriptor's own `load()`. The pairing that `RegisteredView`
 * erased is restored from the same object it was erased from; nothing else can reach either
 * half, because `views` is the only array of descriptors and this is the only projection.
 *
 * What is NOT claimed: that a descriptor whose `select` and `load` disagree would be caught
 * here. It is caught where it is written — `ViewDescriptor<T>` in `features/types.ts` ties the
 * two together, and `editorView` is declared with it. The erasure happens on the way INTO the
 * array, after the compiler has already checked the pair.
 */

/**
 * What every panel receives. `view` is opaque here BY DESIGN: the shell spreads it into a
 * module it has never heard of, and `src/app/` has no business looking inside either.
 */
export interface PanelProps {
  readonly view: unknown;
  readonly api: FeatureApi;
}

export interface ToShellViewOptions {
  /**
   * Rendered INSTEAD of the panel, with `load` never called — ADR-0020 §4's declarative size
   * gate. Supplied by the caller because the sentence is localised and this file does not
   * translate for the shell.
   */
  readonly unavailable?: ShellView<PanelProps>['unavailable'];
}

export function toShellView(
  registered: RegisteredView,
  doc: DocumentSnapshot,
  api: FeatureApi,
  options: ToShellViewOptions = {},
): ShellView<PanelProps> {
  const load = registered.load as () => Promise<{ default: ComponentType<PanelProps> }>;

  return {
    id: registered.id,
    load,
    // PURE and synchronous, memoised by the descriptor itself on snapshot identity
    // (`selectEditorView`), so calling it on every render of the host is an identity check.
    props: { view: registered.select(doc), api },
    ...(options.unavailable === undefined ? {} : { unavailable: options.unavailable }),
  };
}

/** `ViewDescriptor` → `ViewTab`: the label key resolved, `select` and `load` dropped. */
export function toViewTab(registered: RegisteredView, t: Translate): ViewTab {
  return {
    id: registered.id,
    label: t(registered.labelKey),
    icon: registered.icon,
    ...(registered.beta === undefined ? {} : { beta: registered.beta }),
  };
}

/**
 * `true` when this view refuses the document at its current size.
 *
 * Kept here rather than inside `toShellView` so the caller can render a localised explanation
 * — the tab is shown and disabled, and the region says why, which is the whole point of
 * `maxBytes` being declarative.
 */
export function exceedsMaxBytes(registered: RegisteredView, doc: DocumentSnapshot): boolean {
  return registered.maxBytes !== undefined && doc.bytes > registered.maxBytes;
}
